// ─── PREFER-V3-DEDUP (revertable) ───────────────────────────────────────────
// This whole file was added to support showing the V3 row (instead of the V4
// row) when a device exists in both V3 and V4. To fully revert to the previous
// V4-preferred behavior, delete this file and revert every site tagged
// `PREFER-V3-DEDUP` across the codebase (grep for that marker).
// ─────────────────────────────────────────────────────────────────────────────

import Device from "./models/Device";
import { v4AlarmOriginDevice } from "./models/v4AlarmOrigin";
import { evaluateDeviceStatus, mapV4DeviceToV3, mapV4LocationToV3Position } from "../containers/V4ARC/transforms";

/**
 * When a device exists in both V3 and V4 we render the V3 row — including its
 * own (correct) V3 status/location/battery. We overlay ONLY the V4 identity
 * (uuid + owner) so V4-only operations still resolve against the kept V3 row:
 * DevCon, the ARC device/account links, the Emerit "incomplete" badge, and the
 * admin cleanup of the V4 copy. No V4 status/location/battery is carried over,
 * and `v4Type` is not set, so `isFromV4` stays false and the row keeps its V3
 * identity, telemetry, and routing.
 */
export function mergeV4IntoV3(v3Device, v4Device) {
  if (!v3Device || !v4Device) return v3Device || v4Device;

  const read = (key) =>
    typeof v4Device.get === "function" ? v4Device.get(key) : v4Device[key];

  let merged = v3Device;
  const overlay = (key, value) => {
    if (value !== undefined && value !== null && value !== "") {
      merged = merged.set(key, value);
    }
  };

  // V4 identity only — lets V4-only operations (DevCon, ARC links, the Emerit
  // badge, admin cleanup of the V4 copy, V4 account scope) resolve against the
  // kept V3 row. Live status/location/battery are deliberately NOT overlaid —
  // the V3 row shows its own, correct V3 telemetry.
  overlay("uuid", read("uuid"));
  overlay("v4OwnerId", read("v4OwnerId"));

  return merged;
}

// Resolve a device's V4 UUID. V4-fetched / Emerit-overlaid rows already carry it
// on `uuid`; a bare non-Emerit V3 row does not, so match it against the V4-only
// list (the V4 copy the dedup dropped) by id/uuid/uniqueId. Returns null when the
// device has no V4 counterpart — never the V3 numeric id.
export function findV4DeviceUuid(device, v4OnlyDevices) {
  if (!device) return null;
  const read = (d, key) => (typeof d.get === "function" ? d.get(key) : d[key]);
  const own = read(device, "uuid");
  if (own) return own;
  const id = read(device, "id");
  const uniqueId = read(device, "uniqueId");
  const list = v4OnlyDevices && v4OnlyDevices.toArray ? v4OnlyDevices.toArray() : (v4OnlyDevices || []);
  const match = list.find(m =>
    read(m, "uuid") === id || read(m, "id") === id || (uniqueId && read(m, "uniqueId") === uniqueId)
  );
  return match ? read(match, "id") : null;
}

// findV4DeviceUuid for a whole list, keyed by row id. Indexes the V4-only list
// once; a per-device scan is quadratic.
export function findV4DeviceUuids(devices, v4OnlyDevices) {
  const toArray = (l) => (l && l.toArray ? l.toArray() : (l || []));
  const read = (d, key) => (typeof d.get === "function" ? d.get(key) : d[key]);
  const list = toArray(v4OnlyDevices);
  // First position per key, as findV4DeviceUuid returns the first match.
  const byId = new Map();
  const byUniqueId = new Map();
  const index = (map, key, pos) => { if (!map.has(key)) map.set(key, pos); };
  list.forEach((m, pos) => {
    index(byId, read(m, "uuid"), pos);
    index(byId, read(m, "id"), pos);
    if (read(m, "uniqueId")) index(byUniqueId, read(m, "uniqueId"), pos);
  });
  return new Map(toArray(devices).map((d) => {
    const id = read(d, "id");
    const own = read(d, "uuid");
    if (own) return [id, own];
    const uniqueId = read(d, "uniqueId");
    const hits = [byId.get(id), uniqueId ? byUniqueId.get(uniqueId) : undefined]
      .filter((pos) => pos !== undefined);
    return [id, hits.length ? read(list[Math.min(...hits)], "id") : null];
  }));
}

// A V4 alarm/event carries the V4 device uuid in `deviceId`, but the dedup keeps
// the V3 row (uuid overlaid) and drops the V4 copy — so match by id OR uuid,
// mirroring selectDeviceById.
export function deviceMatchesId(device, deviceId) {
  // A falsy id is "no id", not something to match on.
  if (!device || !deviceId) return false;
  const read = (key) =>
    typeof device.get === "function" ? device.get(key) : device[key];
  return read("id") === deviceId || read("uuid") === deviceId;
}

export function findDeviceForAlarmId(devices, deviceId) {
  if (!devices || typeof devices.find !== "function") return undefined;
  return devices.find((d) => deviceMatchesId(d, deviceId));
}

// The row the device list shows for a V4 device uuid (a request receiver's, say). The dedup drops
// the V4 copy of a migrated non-Emerit device and keeps its bare V3 row, matched on uniqueId (IMEI),
// so go through the copy to that row; with no V3 twin the copy itself stands in.
export function findDeviceForV4Id(devices, v4OnlyDevices, deviceId) {
  const listed = findDeviceForAlarmId(devices, deviceId);
  if (listed) return listed;
  const copy = findDeviceForAlarmId(v4OnlyDevices, deviceId);
  if (!copy) return undefined;
  const uniqueId = typeof copy.get === "function" ? copy.get("uniqueId") : copy.uniqueId;
  return (uniqueId && devices?.find?.((d) => deviceMatchesUniqueId(d, uniqueId))) || copy;
}

// IMEI of the alarm's V4 device — the key `selectDevices` matches V3 twins on.
function alarmOriginUniqueId(alarm) {
  const v4Data =
    typeof alarm.get === "function" ? alarm.get("v4Data") : alarm.v4Data;
  const metadata = v4AlarmOriginDevice(v4Data)?.metadata;
  return metadata?.imei || metadata?.unique_id || null;
}

function deviceMatchesUniqueId(device, uniqueId) {
  if (!device || !uniqueId) return false;
  const own =
    typeof device.get === "function" ? device.get("uniqueId") : device.uniqueId;
  return !!own && String(own) === String(uniqueId);
}

// A V3 device's alarm on a V4 ARC is raised by its empty V4 shadow; prefer the V3
// device — named by `v3DeviceId`, else by uuid, else sharing the shadow's IMEI.
export function findDeviceForAlarm(devices, alarm) {
  if (!alarm || !devices || typeof devices.find !== "function") return undefined;
  const read = (key) =>
    typeof alarm.get === "function" ? alarm.get(key) : alarm[key];
  const v3DeviceId = read("v3DeviceId");
  const uniqueId = alarmOriginUniqueId(alarm);
  return (
    (v3DeviceId && findDeviceForAlarmId(devices, v3DeviceId)) ||
    findDeviceForAlarmId(devices, read("deviceId")) ||
    (uniqueId && devices.find((d) => deviceMatchesUniqueId(d, uniqueId))) ||
    undefined
  );
}

// Whether `device` is one `findDeviceForAlarm` could resolve for this alarm.
export function deviceMatchesAlarm(device, alarm) {
  if (!alarm) return false;
  const read = (key) =>
    typeof alarm.get === "function" ? alarm.get(key) : alarm[key];
  return (
    deviceMatchesId(device, read("v3DeviceId")) ||
    deviceMatchesId(device, read("deviceId")) ||
    deviceMatchesUniqueId(device, alarmOriginUniqueId(alarm))
  );
}

// Whether `device` is the V3 device behind a V4 alarm.
export function isV3DeviceOfAlarm(device, alarm) {
  if (!device || !alarm || device.isFromV4) return false;
  const read = (key) =>
    typeof alarm.get === "function" ? alarm.get(key) : alarm[key];
  return (
    deviceMatchesId(device, read("v3DeviceId")) ||
    deviceMatchesUniqueId(device, alarmOriginUniqueId(alarm))
  );
}

// Whether V4 never saw the alarm's device connect (empty `ingress_connection`), so it
// can't tell whether the device is connected.
export function alarmDeviceNeverConnected(alarm) {
  if (!alarm) return false;
  const v4Data =
    typeof alarm.get === "function" ? alarm.get("v4Data") : alarm.v4Data;
  const device = v4AlarmOriginDevice(v4Data);
  if (!device) return false;
  return evaluateDeviceStatus(device.ingress_connection).offlineReason === "never_connected";
}

// True only from a live V4 row: a V3 row, a stand-in or a forwarded device's shadow can't say.
export function alarmDeviceOffline(devices, alarm) {
  if (!alarm?.get?.("v4") || !alarm.isOpen || !alarm.isDeviceRaised || alarm.get("v3AlarmId")) return false;
  const device = findDeviceForAlarm(devices, alarm);
  return !!device?.isFromV4 && !device.connected;
}

// A device-raised V4 alarm embeds its full device payload — rebuild a Device from it
// when the device list lacks it. Null when something other than a device raised it.
export function v4DeviceFromAlarm(alarm) {
  if (!alarm) return null;
  const v4Data =
    typeof alarm.get === "function" ? alarm.get("v4Data") : alarm.v4Data;
  const device = v4AlarmOriginDevice(v4Data);
  if (!device) return null;
  // The snapshot has no position; use the alarm's own fix.
  const position = v4Data?.location && mapV4LocationToV3Position(v4Data.location);
  return new Device({ ...mapV4DeviceToV3(device), ...(position ? { position } : {}) });
}

// A stand-in for a Device that can't be resolved, built from what the alarm names:
// either its device is missing from the snapshot, or no device raised it at all and
// this is the only Device the device-keyed UI will ever get.
export function alarmStandInDevice(alarm) {
  if (!alarm) return null;
  const read = (key) =>
    typeof alarm.get === "function" ? alarm.get(key) : alarm[key];
  // With no device, the alarm's own fix is the only one there is — and both the panel
  // and the map read position off the device. V3 alarms carry no `v4Data`.
  const location = read("v4Data")?.location;
  return new Device({
    id: read("deviceId"),
    name: read("deviceName") || "",
    // Carried so the account still resolves its default protocol, sites and beacons —
    // without it the handle wizard blanks the panic word and drops the operator script.
    accountId: read("accountId"),
    accountName: read("accountName") || "",
    position: (location && mapV4LocationToV3Position(location)) || {},
  });
}
